import {
	Box,
	Button,
	Chip,
	Divider,
	Fade,
	FormGroup,
	Paper,
	TextField,
} from '@mui/material'
import { useFormik } from 'formik'
import { action } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import * as yup from 'yup'
import {
	BaseTandemAccountModel,
	TandemAccount,
} from '../../api/clients/tandem/DTOs'
import { ListControlInput } from '../../modules/FormBuilderInterop/Configuration'
import { FixedHeader, FixedHeaderWithHint } from '../../utils/HOC/FixedHeaders'
import AdministrationPageContainer from '../Organization/Administration/AdministrationPageContainer'
import { AdministrationPageTabs } from '../Organization/Administration/AdministrationPageTabs'

type TandemAccountSettingsSectionProps = {
	account: TandemAccount
	existingAccountNames: string[]
	onUpdateAccount: (accountModel: BaseTandemAccountModel) => void
	tabLabels: string[]
	onTabChanged: (event: React.SyntheticEvent, value: number) => void
}

export const TandemAccountSettingsSection = observer(
	({
		account,
		onUpdateAccount,
		existingAccountNames,
		tabLabels,
		onTabChanged,
	}: TandemAccountSettingsSectionProps) => {
		const localStore = useLocalObservable(() => ({
			acceptedPayees: account.acceptedPayees,
		}))

		const validationSchema = yup.object({
			name: yup
				.string()
				.required('Name is a required field')
				.test(
					'is-unique',
					'A Tandem account with this name already exists in your organization',
					(value) => {
						return (
							(value !== undefined &&
								!existingAccountNames
									.map((v) => v.toUpperCase().trim())
									.includes(value.toUpperCase().trim())) ||
							value === account.name
						)
					},
				),
			description: yup.string(),
		})

		const formik = useFormik({
			initialValues: {
				name: account.name,
				description: account.description,
			},
			validationSchema,
			onSubmit: (values) => {
				onUpdateAccount({
					name: values.name,
					description: values.description,
					acceptedPayees: localStore.acceptedPayees,
				})
			},
		})

		return (
			<AdministrationPageContainer title={'Tandem Account'}>
				<Box display="flex" flexDirection="column" width="100%" height="100%">
					<AdministrationPageTabs
						currentTab={0}
						onTabChanged={onTabChanged}
						tabLabels={tabLabels}
					/>
					<Divider />
					<Fade in>
						<Box
							display="flex"
							flexDirection="column"
							height="100%"
							width="100%"
						>
							<Box
								component={Paper}
								marginY={2}
								paddingY={1}
								paddingX={2}
								width="100%"
								display="flex"
								flexDirection="column"
								gap={2}
							>
								<FixedHeaderWithHint
									label="Name"
									hint={formik.touched.name && formik.errors.name}
									hintColor="error.main"
								>
									<TextField
										fullWidth
										id="name"
										name="name"
										value={formik.values.name}
										error={!!formik.errors.name && formik.touched.name}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										disabled={formik.isSubmitting}
										autoComplete="off"
										size="small"
									/>
								</FixedHeaderWithHint>
								<FixedHeaderWithHint
									label="Description"
									hint={formik.values.description && formik.touched.description}
									hintColor="error.main"
								>
									<TextField
										fullWidth
										id="description"
										name="description"
										value={formik.values.description}
										error={
											!!formik.errors.description && formik.touched.description
										}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										disabled={formik.isSubmitting}
										autoComplete="off"
										size="small"
									/>
								</FixedHeaderWithHint>
								<FixedHeader label="Accepted Payees">
									<FormGroup>
										<ListControlInput
											onAddItem={action((value) =>
												localStore.acceptedPayees.push(value),
											)}
										/>
										<Box display="flex" flexWrap="wrap" gap={1} paddingY={1}>
											{localStore.acceptedPayees.map((v) => (
												<Chip
													key={v}
													label={v}
													onDelete={action(() => {
														localStore.acceptedPayees =
															localStore.acceptedPayees.filter(
																(val) => val !== v,
															)
													})}
												/>
											))}
										</Box>
									</FormGroup>
								</FixedHeader>
							</Box>
							<Box display="flex" flexDirection="row" justifyContent="flex-end">
								<Button
									variant="contained"
									disabled={
										!formik.dirty ||
										!formik.isValid ||
										formik.isSubmitting ||
										localStore.acceptedPayees.length === 0 ||
										JSON.stringify(localStore.acceptedPayees) ===
											JSON.stringify(account.acceptedPayees)
									}
									onClick={formik.submitForm}
								>
									Save Changes
								</Button>
							</Box>
						</Box>
					</Fade>
				</Box>
			</AdministrationPageContainer>
		)
	},
)
